import { useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { PlanOption } from '../../../../types/admin';
import styles from './PlanOptions.module.scss';
import { formatMoney, readyMeshCount } from './pricing';

/** Four plan meshes per option: upper/lower × current/result. */
const PLAN_MESH_SLOTS = 4;

export type PlanRailProps = {
  /** Already in display order: main plan first. */
  readonly options: readonly PlanOption[];
  readonly titles: ReadonlyMap<string, string>;
  readonly activeId: string | null;
  /** Ids whose editor holds edits the server has not accepted yet. */
  readonly dirtyIds: ReadonlySet<string>;
  readonly onSelect: (optionId: string) => void;
  readonly tabId: (optionId: string) => string;
  readonly panelId: (optionId: string) => string;
};

/**
 * Vertical picker for the cycle's plans.
 *
 * Every plan used to be rendered expanded underneath the previous one, so
 * reaching the third alternative meant scrolling past two full editors. The
 * rail keeps all of them one click away and carries the at-a-glance state
 * (counts, total, how many STL files converted, unsaved edits) that admins
 * previously had to open each card to read.
 */
export function PlanRail({
  options,
  titles,
  activeId,
  dirtyIds,
  onSelect,
  tabId,
  panelId,
}: PlanRailProps) {
  const { t } = useTranslation();
  const itemRefs = useRef(new Map<string, HTMLButtonElement | null>());

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const lastIndex = options.length - 1;
    let nextIndex: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = index === lastIndex ? 0 : index + 1;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = index === 0 ? lastIndex : index - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = lastIndex;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const next = options[nextIndex];
    if (!next) return;
    onSelect(next.id);
    itemRefs.current.get(next.id)?.focus();
  }

  return (
    <div
      className={styles.rail}
      role="tablist"
      aria-orientation="vertical"
      aria-label={t('planOptions.plansNavLabel')}
    >
      {options.map((option, index) => {
        const isActive = option.id === activeId;
        const breakdown = option.pricing_breakdown;
        const ready = readyMeshCount(option);
        const dash = '—';
        return (
          <button
            key={option.id}
            type="button"
            ref={(node) => {
              itemRefs.current.set(option.id, node);
            }}
            id={tabId(option.id)}
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId(option.id)}
            tabIndex={isActive ? 0 : -1}
            className={`${styles.railItem} ${isActive ? styles.railItemActive : ''}`}
            onClick={() => onSelect(option.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid="plan-rail-item"
          >
            <span className={styles.railTitleRow}>
              <span className={styles.railTitle}>{titles.get(option.id) ?? ''}</span>
              {dirtyIds.has(option.id) && (
                <span
                  className={styles.railDirtyDot}
                  title={t('planOptions.unsavedChanges')}
                  aria-label={t('planOptions.unsavedChanges')}
                  data-testid="plan-rail-dirty"
                />
              )}
            </span>

            <span className={styles.railMeta}>
              <span>
                {t('planOptions.railCounts', {
                  upper: option.upper_aligners_count ?? dash,
                  lower: option.lower_aligners_count ?? dash,
                })}
              </span>
              <span className={styles.railTotal}>
                {breakdown
                  ? formatMoney(Number(breakdown.amount_due), breakdown.currency)
                  : t('planOptions.railNoPrice')}
              </span>
            </span>

            <span className={styles.railTags}>
              {option.is_main && (
                <span className={`${styles.railTag} ${styles.railTagMain}`}>
                  {t('planOptions.mainBadge')}
                </span>
              )}
              {option.is_selected && (
                <span className={`${styles.railTag} ${styles.railTagSelected}`}>
                  {t('planOptions.selectedBadge')}
                </span>
              )}
              {!option.is_published && (
                <span className={styles.railTag}>{t('planOptions.unpublished')}</span>
              )}
              <span
                className={`${styles.railTag} ${
                  ready === PLAN_MESH_SLOTS ? styles.railTagReady : ''
                }`}
              >
                {t('planOptions.railMeshes', { ready, total: PLAN_MESH_SLOTS })}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
